import { themeAtom } from "../../shared/atoms";
import Datepicker from "../../shared/components/Datepicker/Datepicker";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { CustomDatePickerInput } from "../components/CustomDatePicker";
import { TechFormSectionLoadError } from "../components/TechFormSectionLoadError";
import type { TechFormPageProps } from "../models/tech-form-page.models";
import { TECH_FORM_ENDPOINTS } from "../models/tech-form-section-data.constants";
import { TECH_FORM_CACHE_MODES } from "../models/tech-form-vessel-cache.constants";
import {
  Tq5PortCallsM,
  mapSqlToTq5PortCallsForm,
  tq5Options,
  tq5PortCallsFormInit,
} from "../models/tq5-port-calls.models";
import { OptionType } from "../models/types";
import { isTechFormSectionDataCached, loadTechFormEndpointData } from "../services/tech-form-section-data.service";
import { getTechFormPortOptions } from "../services/tech-forms.service";
import { tq5PortCallsAtom } from "../state/tech-form.atoms";
import { ReactSelectStyling } from "../utils/TechUtils";
import { useAtom, useAtomValue } from "jotai";
import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useParams } from "react-router-dom";
import Select from "react-select";
import { twMerge } from "tailwind-merge";

export const Tq5PortCalls: React.FC<TechFormPageProps> = ({ isCompletedForm, cacheInitialForm, onSectionReady }) => {
  const theme = useAtomValue(themeAtom);
  const [storedForm, setStoredForm] = useAtom(tq5PortCallsAtom);

  const { imoNumber, vesselName } = useParams();
  const [isLoading, setLoading] = useState(false);
  const [isSectionDataReady, setIsSectionDataReady] = useState(false);
  const [sectionLoadError, setSectionLoadError] = useState(false);
  const [sectionLoadAttempt, setSectionLoadAttempt] = useState(0);
  const [portOptions, setPortOptions] = useState<OptionType[]>([]);
  const [portOptionsLoaded, setPortOptionsLoaded] = useState(false);
  const [isLoadingPortOptions, setIsLoadingPortOptions] = useState(false);

  const [form, setForm] = useState<Tq5PortCallsM>(() => {
    return Object.keys(storedForm ?? {}).length > 0 ? storedForm : tq5PortCallsFormInit;
  });

  useEffect(() => {
    let ignoreResult = false;
    setSectionLoadError(false);
    const _form = storedForm ?? {};
    const storageisEmpty = Object.keys(_form).length === 0;
    if (isCompletedForm) {
      void fetchData(() => ignoreResult);
    } else {
      if (storageisEmpty) {
        void fetchData(() => ignoreResult);
      } else {
        setForm(_form);
        cacheInitialForm(_form);
        setIsSectionDataReady(true);
        onSectionReady?.();
      }
    }

    return () => {
      ignoreResult = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imoNumber, vesselName, isCompletedForm, sectionLoadAttempt]);

  const fetchData = async (isStale: () => boolean) => {
    if (imoNumber && vesselName) {
      try {
        const requestIdentity = {
          imoNumber,
          vesselName,
          mode: isCompletedForm ? TECH_FORM_CACHE_MODES.completed : TECH_FORM_CACHE_MODES.editable,
        };
        if (!isTechFormSectionDataCached({ ...requestIdentity, section: 5 })) {
          setLoading(true);
        }
        const result = await loadTechFormEndpointData<any[]>({
          ...requestIdentity,
          endpoint: TECH_FORM_ENDPOINTS.lastPortCalls,
        });
        if (isStale()) return;
        const _portCalls = result;

        const portCalls = mapSqlToTq5PortCallsForm(_portCalls);
        const loadedPortCallsForm: Tq5PortCallsM = { rows: portCalls };

        setForm(loadedPortCallsForm);
        setStoredForm(loadedPortCallsForm);
        cacheInitialForm(loadedPortCallsForm);
        setIsSectionDataReady(true);
        onSectionReady?.();
      } catch (error: unknown) {
        if (!isStale()) {
          setSectionLoadError(true);
          console.error("Unable to load Technical Form port calls", error);
        }
      } finally {
        if (!isStale()) {
          setLoading(false);
        }
      }
    }
  };

  const handlePortSelectChange = (port: OptionType | null, rowIndex: number) => {
    setForm((prevData) => {
      const updatedRows = [...prevData.rows];
      const updatedRow = { ...updatedRows[rowIndex], port: port?.label ?? "" };
      updatedRows[rowIndex] = updatedRow;
      return { rows: updatedRows };
    });
  };

  const handleSelectChange = (activity: { value: string; label: string } | null, rowIndex: number) => {
    setForm((prevData) => {
      const updatedRows = [...prevData.rows];
      updatedRows[rowIndex] = { ...updatedRows[rowIndex], activity };
      return { rows: updatedRows };
    });
  };

  const updateDate = (date: Date | null, rowIndex: number, isFromDate = true) => {
    setForm((prevData) => {
      const updatedRows = [...prevData.rows];
      const dateField = isFromDate ? "fromDate" : "toDate";
      updatedRows[rowIndex] = { ...updatedRows[rowIndex], [dateField]: date };
      return { rows: updatedRows };
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  // Storage
  useEffect(() => {
    if (isSectionDataReady) {
      setStoredForm(form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isCompletedForm, isSectionDataReady]);

  const selectStyles = ReactSelectStyling();

  const extraStyles = {
    ...selectStyles,
    control: (base: any, state: any) => ({
      ...selectStyles.control(base, state),
      height: 48,
    }),
  };

  const loadPortOptions = async () => {
    if (portOptionsLoaded || isLoadingPortOptions) {
      return;
    }

    setIsLoadingPortOptions(true);
    try {
      setPortOptions(await getTechFormPortOptions());
      setPortOptionsLoaded(true);
    } catch (error) {
      console.error("Error loading tech form port options", error);
    } finally {
      setIsLoadingPortOptions(false);
    }
  };

  const optionForPort = (value: string) =>
    portOptions.find((option) => option.value === value || option.label === value) ??
    (value ? { value, label: value } : null);

  if (sectionLoadError) {
    return <TechFormSectionLoadError onRetry={() => setSectionLoadAttempt((attempt) => attempt + 1)} />;
  }

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorClassName="text-green" loadingIndicatorSize="lg">
      <div
        className={twMerge(
          "p-2 md:p-4",
          "[&_input]:border",
          "[&_select]:text-sm",
          theme.home.tailwind.panelBackground,
          theme.home.tailwind.textDefault,
          theme.balticQuestionnaire.labelsText,
          theme.balticQuestionnaire.inputsBg,
          theme.balticQuestionnaire.inputsText,
          theme.balticQuestionnaire.inputsBorder,
          theme.balticQuestionnaire.selectsBg,
          theme.balticQuestionnaire.selectsBorder,
        )}
      >
        <h1 className="custom-heading">Port Calls</h1>
        <form onSubmit={handleSubmit}>
          <table className="w-50 text-left text-sm">
            <thead className="text-sm font-medium">
              <tr className="flex items-baseline">
                <th scope="col" className="flex w-[60px] justify-center py-3">
                  #
                </th>
                <th scope="col" className="flex w-[220px] justify-center py-3">
                  <label>Port</label>
                </th>
                <th scope="col" className="flex w-[220px] justify-center py-3">
                  <label>Activity</label>
                </th>
                <th scope="col" className="flex w-[250px] justify-center py-3">
                  <label>From</label>
                </th>
                <th scope="col" className="flex w-[250px] justify-center py-3">
                  <label>To</label>
                </th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 15 }, (_, index) => (
                <tr key={index} className="flex items-baseline">
                  <td>
                    <div
                      className={twMerge(
                        "flex w-[48px] items-center justify-center rounded-lg border p-3",
                        theme.uwhcAssistant.inputBorder,
                        theme.uwhcAssistant.tableBg,
                        theme.vesselDetails.info,
                      )}
                    >
                      {index + 1}
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Select
                        className="min-w-[220px]"
                        classNamePrefix="rs"
                        data-test={`tq5-port-call-port-select-${index}`}
                        inputId={`rows${index}Port`}
                        name={`tq5PortCalls.rows[${index}].port`}
                        options={portOptions}
                        value={optionForPort(form.rows[index].port)}
                        onChange={(port) => handlePortSelectChange(port, index)}
                        onMenuOpen={loadPortOptions}
                        isLoading={isLoadingPortOptions}
                        isSearchable
                        placeholder="Port name..."
                        styles={extraStyles}
                      />
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Select
                        classNamePrefix="rs"
                        options={tq5Options}
                        isSearchable={false}
                        placeholder="Select an option"
                        value={tq5Options.find((option) => option.value === form.rows[index].activity?.value)}
                        onChange={(activity) => handleSelectChange(activity, index)}
                        styles={extraStyles}
                      />
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    {isCompletedForm ? (
                      <div className="date-picker-container">
                        <DatePicker
                          selected={
                            form.rows[index].fromDate !== null
                              ? new Date(form.rows[index].fromDate as unknown as string)
                              : null
                          }
                          onChange={(date) => updateDate(date, index, true)}
                          dateFormat="MM/dd/yyyy"
                          customInput={
                            <CustomDatePickerInput
                              value={
                                form.rows[index].fromDate
                                  ? new Date(form.rows[index].fromDate as unknown as string).toLocaleDateString()
                                  : ""
                              }
                              onClick={(e) => e.preventDefault()}
                              onChange={(e) => {}}
                            />
                          }
                          withPortal
                          showYearDropdown
                          scrollableYearDropdown
                          yearDropdownItemNumber={15}
                          className="focus:outline-none"
                        />
                      </div>
                    ) : (
                      <Datepicker
                        maxDate={form.rows[index].toDate}
                        selected={
                          form.rows[index].fromDate !== null
                            ? new Date(form.rows[index].fromDate as unknown as string)
                            : null
                        }
                        onChange={(date: Date) => updateDate(date, index, true)}
                        className="focus:outline-none"
                      />
                    )}
                  </td>

                  <td className="px-6 py-4">
                    {isCompletedForm ? (
                      <div className="date-picker-container">
                        <DatePicker
                          selected={
                            form.rows[index].toDate !== null
                              ? new Date(form.rows[index].toDate as unknown as string)
                              : null
                          }
                          onChange={(date) => updateDate(date, index, false)}
                          dateFormat="MM/dd/yyyy"
                          customInput={
                            <CustomDatePickerInput
                              value={
                                form.rows[index].toDate
                                  ? new Date(form.rows[index].toDate as unknown as string).toLocaleDateString()
                                  : ""
                              }
                              onClick={(e) => e.preventDefault()}
                              onChange={(e) => {}}
                            />
                          }
                          withPortal
                          showYearDropdown
                          scrollableYearDropdown
                          yearDropdownItemNumber={15}
                          maxDate={
                            form.rows[index].fromDate ? new Date(form.rows[index].fromDate as unknown as string) : null
                          }
                          className="focus:outline-none"
                        />
                      </div>
                    ) : (
                      <Datepicker
                        minDate={form.rows[index].fromDate}
                        selected={
                          form.rows[index].toDate !== null
                            ? new Date(form.rows[index].toDate as unknown as string)
                            : null
                        }
                        onChange={(date: Date) => updateDate(date, index, false)}
                        className="focus:outline-none"
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </form>
      </div>
    </LoadingWrapper>
  );
};
